import { useEffect, useState } from "react";
import api from "../lib/api";
import { Briefcase, LineChart, Landmark, Globe2, Newspaper } from "lucide-react";

const BUCKETS = [
  { key: "business_opportunities", label: "Business Opportunities", icon: Briefcase, tag: "BizOps" },
  { key: "market_predictions", label: "Market Predictions", icon: LineChart, tag: "Markets" },
  { key: "political_outcomes", label: "Political Outcomes", icon: Landmark, tag: "Politics" },
  { key: "world_events", label: "World Events", icon: Globe2, tag: "World" },
];

export default function Intel() {
  const [data, setData] = useState({});
  const [news, setNews] = useState([]);
  const [active, setActive] = useState("business_opportunities");

  useEffect(() => {
    api.get("/intel").then(r => setData(r.data));
    api.get("/news").then(r => setNews(r.data));
  }, []);

  const items = data[active] || [];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="intel-page">
      <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Radical Intel</div>
      <h1 className="display-black text-5xl lg:text-6xl uppercase text-[#064E3B] mt-3 tracking-tighter max-w-4xl">
        Business. Markets. Politics. World.
      </h1>
      <p className="text-gray-700 mt-4 max-w-2xl">
        Curated intelligence for operators and partners. Spot the next opportunity before the market sees it.
      </p>

      <div className="mt-10 flex flex-wrap gap-3" data-testid="intel-tabs">
        {BUCKETS.map(b => {
          const Icon = b.icon;
          const isActive = active === b.key;
          return (
            <button
              key={b.key}
              onClick={() => setActive(b.key)}
              className={`flex items-center gap-2 px-5 py-3 border ${isActive ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white text-[#064E3B] border-gray-300 hover:border-[#064E3B]"} mono text-[11px] tracking-[0.18em] uppercase`}
              data-testid={`intel-tab-${b.key}`}
            >
              <Icon className="w-4 h-4" /> {b.label}
            </button>
          );
        })}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-300 border border-gray-300" data-testid="intel-grid">
        {items.map(item => (
          <article key={item.id} className="bento-card p-7" data-testid={`intel-${item.id}`}>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-gray-500">
              {item.region || item.horizon || "—"} · {item.date}
            </div>
            <h3 className="display-black text-2xl uppercase text-[#064E3B] mt-3 leading-tight">{item.title}</h3>
            <p className="text-sm text-gray-700 mt-3">{item.summary}</p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {item.tag && <span className="mono text-[10px] tracking-wider uppercase px-2 py-1 bg-[#D97706] text-white">{item.tag}</span>}
              {item.confidence && <span className="mono text-[10px] tracking-wider uppercase px-2 py-1 border border-[#064E3B] text-[#064E3B]">Confidence: {item.confidence}</span>}
              {item.stage && <span className="mono text-[10px] tracking-wider uppercase px-2 py-1 border border-gray-400 text-gray-700">Stage: {item.stage}</span>}
            </div>
          </article>
        ))}
      </div>

      {/* News strip */}
      <section className="mt-20" data-testid="intel-news-strip">
        <div className="flex items-center gap-3">
          <Newspaper className="w-5 h-5 text-[#D97706]" />
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Newsroom</div>
        </div>
        <h2 className="display-black text-3xl lg:text-4xl uppercase text-[#064E3B] mt-3">Industry pulse.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-300 border border-gray-300">
          {news.slice(0, 6).map(n => (
            <div key={n.id} className="bento-card p-6">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-gray-500">{n.region} · {n.published}</div>
              <h4 className="display-black text-lg uppercase text-[#064E3B] mt-3 leading-tight">{n.title}</h4>
              <p className="text-sm text-gray-700 mt-2">{n.summary}</p>
              <div className="mono text-[10px] tracking-wider uppercase text-[#D97706] mt-3">— {n.source}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
